import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ChevronRight, ChevronLeft, Check, Loader2, User, FileText,
  DollarSign, Paperclip, Home, Users, ClipboardList, Sparkles,
  Send, Upload, LogOut, ChevronDown, PlusCircle, Trash2, AlertCircle,
  X, CheckCircle2, RefreshCw, Clock, MessageCircle, MapPin, Building2,
} from 'lucide-react';
import apiClient from '../services/authService';
import { chatService } from '../services/chatService';

// ── constants ──────────────────────────────────────────────────────────────────
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Self-employed', 'Contract', 'Other'];

const STEPS = [
  { label: 'Personal Info',     key: 'pii',             Icon: User },
  { label: "Driver's License",  key: 'driversLicense',  Icon: FileText },
  { label: 'Employment',        key: 'income',          Icon: DollarSign },
  { label: 'Documents',         key: 'incomeDocuments', Icon: Paperclip },
  { label: 'Rental History',    key: 'rentalHistory',   Icon: Home },
  { label: 'References',        key: 'references',      Icon: Users },
  { label: 'Review',            key: 'review',          Icon: ClipboardList },
];

const BLANK_RENTAL = { address: '', landlordName: '', landlordPhone: '', monthlyRent: '', moveInDate: '', moveOutDate: '', reasonForLeaving: '' };
const BLANK_REF    = { name: '', relationship: '', phone: '', email: '' };

// ── format/mask helpers ────────────────────────────────────────────────────────

// Parses a YYYY-MM-DD string as a LOCAL date to avoid UTC midnight offset issues.
function parseLocalDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// Auto-masks SSN to XXX-XX-XXXX as the user types, stripping non-digits.
function formatSSN(raw) {
  const d = raw.replace(/\D/g, '').slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

// Auto-masks phone to (XXX) XXX-XXXX as the user types, stripping non-digits.
function formatPhone(raw) {
  const d = raw.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ── small helpers ──────────────────────────────────────────────────────────────
function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

function Input({ className = '', ...props }) {
  return (
    <input
      className={`w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent bg-white ${className}`}
      {...props}
    />
  );
}

function Select({ children, className = '', ...props }) {
  return (
    <select
      className={`w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white ${className}`}
      {...props}
    >
      {children}
    </select>
  );
}

// ── main component ─────────────────────────────────────────────────────────────
export default function ApplyForm({ user, onLogout, onLogin }) {
  const navigate  = useNavigate();
  const fileRef   = useRef(null);
  const aiEndRef  = useRef(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // application data
  const [application, setApplication] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [submitted, setSubmitted]     = useState(false);

  // stepper
  const [step, setStep] = useState(0);

  // per-section form state
  const [pii, setPii]           = useState({ firstName: '', lastName: '', dob: '', ssn: '', phone: '', currentAddress: '' });
  const [dl, setDl]             = useState({ licenseNumber: '', issuingState: '', expiryDate: '' });
  const [dlFile, setDlFile]     = useState(null);
  const [dlUploading, setDlUploading] = useState(false);
  const [income, setIncome]     = useState({ employer: '', jobTitle: '', annualIncome: '', employmentType: 'Full-time', startDate: '', contactName: '', contactPhone: '', contactEmail: '' });
  const [incDocs, setIncDocs]   = useState([]);
  const [incUpload, setIncUpload] = useState(null);
  const [incUploading, setIncUploading] = useState(false);
  const [firstTimeRenter, setFirstTimeRenter] = useState(false);
  const [rentals, setRentals]   = useState([{ ...BLANK_RENTAL }]);
  const [refs, setRefs]         = useState([{ ...BLANK_REF }, { ...BLANK_REF }]);
  const [fieldErrors, setFieldErrors] = useState({});

  // AI panel
  const [aiOpen, setAiOpen]     = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput]   = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSessionId]           = useState(() => `apply-ai-${Date.now()}`);

  // ── bootstrap ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user || user.userType !== 'Applicant') { navigate('/apply'); return; }
    loadApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    aiEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages, aiLoading]);

  useEffect(() => {
    function handler(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  async function loadApplication() {
    try {
      const res = await apiClient.get('/api/applications/mine');
      const app = res.data;
      setApplication(app);
      prefill(app);
      // resume at first incomplete step
      const keys = STEPS.map(s => s.key);
      const firstIncomplete = keys.findIndex(k => k !== 'review' && !app.completedSections?.includes(k));
      setStep(firstIncomplete >= 0 ? firstIncomplete : STEPS.length - 1);
    } catch {
      // no application yet — stay at step 0
    } finally {
      setLoading(false);
    }
  }

  function prefill(app) {
    if (app.pii)            setPii(p => ({ ...p, ...app.pii, ssn: '' }));
    if (app.driversLicense) setDl(d => ({ ...d, ...app.driversLicense, licenseNumber: '' }));
    if (app.income)         setIncome(i => ({ ...i, ...app.income }));
    if (app.incomeDocuments?.length) setIncDocs(app.incomeDocuments);
    if (app.rentalHistory?.length)   { setRentals(app.rentalHistory); setFirstTimeRenter(false); }
    else if (app.completedSections?.includes('rentalHistory')) setFirstTimeRenter(true);
    if (app.references?.length) setRefs(app.references);
    if (['submitted', 'under_review', 'approved', 'rejected'].includes(app.status)) setSubmitted(true);
  }

  // ── validation ─────────────────────────────────────────────────────────────
  function validate() {
    const errs = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (step === 0) {
      if (!pii.firstName.trim()) errs.firstName = 'Required';
      if (!pii.lastName.trim())  errs.lastName  = 'Required';

      // DOB — required + must be past + age 18–120
      if (!pii.dob) {
        errs.dob = 'Required';
      } else {
        const dob = parseLocalDate(pii.dob);
        const age = today.getFullYear() - dob.getFullYear() -
          (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
        if (dob >= today)  errs.dob = 'Date of birth must be in the past';
        else if (age < 18) errs.dob = 'You must be at least 18 years old to apply';
        else if (age > 120) errs.dob = 'Enter a valid date of birth';
      }

      // SSN — required (if not already saved) + format XXX-XX-XXXX
      const ssnAlreadySaved = application?.completedSections?.includes('pii');
      if (!ssnAlreadySaved) {
        if (!pii.ssn.trim()) {
          errs.ssn = 'Required';
        } else if (!/^\d{3}-\d{2}-\d{4}$/.test(pii.ssn)) {
          errs.ssn = 'Enter a valid SSN (XXX-XX-XXXX)';
        }
      }

      // Phone — required + 10 digits
      if (!pii.phone.trim()) {
        errs.phone = 'Required';
      } else if (pii.phone.replace(/\D/g, '').length !== 10) {
        errs.phone = 'Enter a valid 10-digit US phone number';
      }

      if (!pii.currentAddress.trim()) errs.currentAddress = 'Required';
    }

    if (step === 1) {
      const dlSaved = application?.completedSections?.includes('driversLicense');
      if (!dlSaved && !dl.licenseNumber.trim()) errs.licenseNumber = 'Required';
      if (!dl.issuingState) errs.issuingState = 'Required';

      // DL expiry — required + must not be expired
      if (!dl.expiryDate) {
        errs.expiryDate = 'Required';
      } else if (parseLocalDate(dl.expiryDate) < today) {
        errs.expiryDate = "Driver's license is expired";
      }
    }

    if (step === 2) {
      if (!income.employer.trim())  errs.employer     = 'Required';
      if (!income.jobTitle.trim())  errs.jobTitle     = 'Required';
      if (!income.annualIncome)     errs.annualIncome = 'Required';
      if (!income.employmentType)   errs.employmentType = 'Required';

      // Employment start — required + must be past or today
      if (!income.startDate) {
        errs.startDate = 'Required';
      } else {
        const start = parseLocalDate(income.startDate);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (start >= tomorrow) errs.startDate = 'Employment start date cannot be in the future';
      }
    }

    if (step === 3) {
      if (incDocs.length === 0) errs.incDocs = 'At least one income document is required';
    }

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  // ── save section ───────────────────────────────────────────────────────────
  async function saveSection() {
    const sectionMap = [
      { section: 'pii',           data: pii },
      { section: 'driversLicense',data: dl },
      { section: 'income',        data: { ...income, annualIncome: parseFloat(income.annualIncome) || 0 } },
      null, // incomeDocuments — saved via upload, not here
      { section: 'rentalHistory', data: firstTimeRenter ? [] : rentals },
      { section: 'references',    data: refs },
    ];
    const entry = sectionMap[step];
    if (!entry) return; // doc step handled separately
    try {
      await apiClient.patch('/api/applications/me/section', entry);
      // refresh application state
      const res = await apiClient.get('/api/applications/mine');
      setApplication(res.data);
    } catch (e) {
      throw new Error(e.response?.data?.error || 'Save failed');
    }
  }

  // ── navigation ─────────────────────────────────────────────────────────────
  async function handleNext() {
    setError('');
    if (!validate()) return;
    if (step < STEPS.length - 1) {
      setSaving(true);
      try {
        await saveSection();
        setStep(s => s + 1);
      } catch (e) {
        setError(e.message);
      } finally {
        setSaving(false);
      }
    }
  }

  function handleBack() {
    setError('');
    setFieldErrors({});
    setStep(s => Math.max(0, s - 1));
  }

  async function handleSubmit() {
    setError('');
    setSaving(true);
    try {
      await apiClient.post('/api/applications/me/submit');
      setSubmitted(true);
    } catch (e) {
      setError(e.response?.data?.error || 'Submission failed');
    } finally {
      setSaving(false);
    }
  }

  // ── file upload helper ─────────────────────────────────────────────────────
  async function uploadFile(file, docType) {
    if (!application?._id) throw new Error('Application not found');
    const urlRes = await apiClient.post('/api/applications/me/upload-url', {
      fileName: file.name, contentType: file.type,
      applicationId: application._id, docType,
    });
    if (!urlRes.data.uploadUrl) throw new Error('Failed to get upload URL');
    await fetch(urlRes.data.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
    await apiClient.post('/api/applications/me/add-document', {
      docType, fileUrl: urlRes.data.blobPath,
      fileName: file.name, fileType: file.type,
    });
    return urlRes.data.blobPath;
  }

  async function handleDlUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setDlFile(file);
    setDlUploading(true);
    try {
      await uploadFile(file, 'dl');
      const res = await apiClient.get('/api/applications/mine');
      setApplication(res.data);
    } catch (err) {
      setError('DL upload failed: ' + (err.message || 'Unknown error'));
    } finally {
      setDlUploading(false);
    }
  }

  async function handleIncomeDocUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setIncUpload(file);
    setIncUploading(true);
    try {
      await uploadFile(file, 'income');
      const res = await apiClient.get('/api/applications/mine');
      setApplication(res.data);
      setIncDocs(res.data.incomeDocuments || []);
      setFieldErrors(fe => ({ ...fe, incDocs: undefined }));
    } catch (err) {
      setError('Upload failed: ' + (err.message || 'Unknown error'));
    } finally {
      setIncUploading(false);
      setIncUpload(null);
    }
  }

  // ── AI panel ───────────────────────────────────────────────────────────────
  async function handleAiSend(e) {
    e?.preventDefault();
    const text = aiInput.trim();
    if (!text || aiLoading) return;
    setAiMessages(m => [...m, { role: 'user', content: text }]);
    setAiInput('');
    setAiLoading(true);
    try {
      const res = await chatService.sendMessage(aiSessionId, text, user?._id);
      setAiMessages(m => [...m, { role: 'assistant', content: res.aiMessage?.content || '' }]);
    } catch {
      setAiMessages(m => [...m, { role: 'assistant', content: 'Sorry, I had trouble with that. Please try again.' }]);
    } finally {
      setAiLoading(false);
    }
  }

  function logout() {
    if (onLogout) onLogout();
    navigate('/apply');
  }

  // ── step renders ───────────────────────────────────────────────────────────
  function renderStepContent() {
    switch (step) {
      case 0: return <StepPii pii={pii} setPii={setPii} errors={fieldErrors} saved={application?.completedSections?.includes('pii')} />;
      case 1: return <StepDL dl={dl} setDl={setDl} errors={fieldErrors} saved={application?.completedSections?.includes('driversLicense')} dlFile={dlFile} dlUploading={dlUploading} onUpload={handleDlUpload} dlDoc={application?.driversLicense?.documentUrl} />;
      case 2: return <StepIncome income={income} setIncome={setIncome} errors={fieldErrors} />;
      case 3: return <StepDocs incDocs={incDocs} incUpload={incUpload} incUploading={incUploading} onUpload={handleIncomeDocUpload} errors={fieldErrors} />;
      case 4: return <StepRentalHistory rentals={rentals} setRentals={setRentals} firstTime={firstTimeRenter} setFirstTime={setFirstTimeRenter} errors={fieldErrors} />;
      case 5: return <StepReferences refs={refs} setRefs={setRefs} errors={fieldErrors} />;
      case 6: return <StepReview application={application} pii={pii} dl={dl} income={income} incDocs={incDocs} rentals={rentals} firstTime={firstTimeRenter} refs={refs} onEdit={setStep} />;
      default: return null;
    }
  }

  // ── guard states ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f4f0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1D9E75] animate-spin" />
      </div>
    );
  }

  if (submitted) {
    return (
      <ApplicationStatusPage
        user={user}
        application={application}
        pii={pii}
        onLogout={logout}
        onLogin={onLogin}
        userMenuOpen={userMenuOpen}
        setUserMenuOpen={setUserMenuOpen}
        userMenuRef={userMenuRef}
      />
    );
  }

  const completed = application?.completedSections || [];
  const isLastStep = step === STEPS.length - 1;

  return (
    <div className="min-h-screen bg-[#f4f4f0] flex flex-col">
      <Header user={user} onLogout={logout} application={application} userMenuOpen={userMenuOpen} setUserMenuOpen={setUserMenuOpen} userMenuRef={userMenuRef} />

      <div className="flex flex-1 overflow-hidden">
        {/* ── form column ── */}
        <div className={`flex-1 overflow-y-auto transition-all duration-300 ${aiOpen ? 'lg:mr-80' : 'lg:mr-12'}`}>
          <div className="max-w-2xl mx-auto px-4 py-8">

            {/* property context banner */}
            {application?.property && (
              <div className="mb-7 bg-white rounded-2xl border border-gray-200 px-5 py-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#1a1a2e] flex items-center justify-center flex-shrink-0">
                  <Home className="w-5 h-5 text-[#1D9E75]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Applying for</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {application.property.name || application.property.address}
                  </p>
                  {application.property.name && application.property.address && (
                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 truncate">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      {application.property.address}
                    </p>
                  )}
                </div>
                {(application.organization?.companyName || application.organization?.name) && (
                  <div className="hidden sm:flex flex-col items-end gap-0.5 flex-shrink-0">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Managed by</p>
                    <div className="flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-gray-400" />
                      <p className="text-xs font-medium text-gray-700">
                        {application.organization.companyName || application.organization.name}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* step progress */}
            <div className="flex items-center mb-8 overflow-x-auto pb-1">
              {STEPS.map((s, i) => {
                const done = completed.includes(s.key) || (s.key === 'review' && completed.length === 6);
                const active = i === step;
                return (
                  <React.Fragment key={s.key}>
                    <button
                      onClick={() => { setError(''); setFieldErrors({}); setStep(i); }}
                      className={`flex-shrink-0 flex flex-col items-center gap-1 group`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                        done  ? 'bg-[#1D9E75] text-white' :
                        active ? 'bg-[#1a1a2e] text-white' :
                                 'bg-white border-2 border-gray-200 text-gray-400'
                      }`}>
                        {done ? <Check className="w-4 h-4" /> : i + 1}
                      </div>
                      <span className={`text-[10px] font-medium whitespace-nowrap ${active ? 'text-[#1a1a2e]' : 'text-gray-400'}`}>{s.label}</span>
                    </button>
                    {i < STEPS.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-1 mt-[-12px] transition-colors ${done ? 'bg-[#1D9E75]' : 'bg-gray-200'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* section card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                {React.createElement(STEPS[step].Icon, { className: 'w-5 h-5 text-[#1D9E75]' })}
                <h2 className="text-lg font-semibold text-gray-900">{STEPS[step].label}</h2>
              </div>

              {renderStepContent()}

              {error && (
                <div className="mt-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
                <button
                  onClick={handleBack}
                  disabled={step === 0}
                  className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                {isLastStep ? (
                  <button
                    onClick={handleSubmit}
                    disabled={saving}
                    className="flex items-center gap-2 bg-[#1D9E75] hover:bg-[#178a65] text-white text-sm font-medium px-6 py-2.5 rounded-xl transition-colors disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Submit Application
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    disabled={saving}
                    className="flex items-center gap-2 bg-[#1D9E75] hover:bg-[#178a65] text-white text-sm font-medium px-6 py-2.5 rounded-xl transition-colors disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Save & Continue <ChevronRight className="w-4 h-4" /></>}
                  </button>
                )}
              </div>
            </div>

            <p className="text-center text-[11px] text-gray-400 mt-4">
              Your information is encrypted and only shared with your prospective landlord.
            </p>
          </div>
        </div>

        {/* ── AI panel ── */}
        <div className={`fixed right-0 top-0 bottom-0 z-40 flex transition-all duration-300 ${aiOpen ? 'w-80' : 'w-12'}`}>
          {/* collapsed strip */}
          {!aiOpen && (
            <button
              onClick={() => setAiOpen(true)}
              className="w-12 bg-[#1a1a2e] flex flex-col items-center justify-center gap-2 hover:bg-[#2a2a3e] transition-colors border-l border-gray-800"
            >
              <Sparkles className="w-4 h-4 text-[#1D9E75]" />
              <span className="text-[10px] font-semibold text-gray-400 tracking-widest" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>ASK NIVA</span>
            </button>
          )}

          {/* expanded panel */}
          {aiOpen && (
            <div className="w-80 bg-white shadow-2xl border-l border-gray-200 flex flex-col">
              <div className="bg-[#1a1a2e] px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#1D9E75]" />
                  <span className="text-sm font-semibold text-white">Ask Niva</span>
                </div>
                <button onClick={() => setAiOpen(false)} className="text-gray-400 hover:text-white transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {aiMessages.length === 0 && (
                  <div className="text-center py-8">
                    <Sparkles className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                    <p className="text-xs text-gray-400">Hi! I'm Niva. Ask me anything about your application.</p>
                  </div>
                )}
                {aiMessages.map((m, i) => (
                  <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white ${m.role === 'user' ? 'bg-gray-300' : 'bg-[#1a1a2e]'}`}>
                      {m.role === 'user' ? <User className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
                    </div>
                    <div className={`max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed ${m.role === 'user' ? 'bg-[#1D9E75] text-white rounded-tr-sm' : 'bg-gray-100 text-gray-800 rounded-tl-sm'}`}>
                      {m.role === 'assistant'
                        ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                        : m.content}
                    </div>
                  </div>
                ))}
                {aiLoading && (
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#1a1a2e] flex items-center justify-center"><Sparkles className="w-3 h-3 text-white" /></div>
                    <div className="bg-gray-100 rounded-xl rounded-tl-sm px-3 py-2 flex gap-1">
                      {[0,150,300].map(d => <div key={d} className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
                    </div>
                  </div>
                )}
                <div ref={aiEndRef} />
              </div>

              {/* input */}
              <form onSubmit={handleAiSend} className="p-3 border-t border-gray-100 flex gap-2">
                <input
                  value={aiInput}
                  onChange={e => setAiInput(e.target.value)}
                  placeholder="Ask a question…"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
                <button type="submit" disabled={aiLoading || !aiInput.trim()} className="p-2 bg-[#1D9E75] text-white rounded-lg disabled:opacity-40">
                  <Send className="w-3.5 h-3.5" />
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── header ────────────────────────────────────────────────────────────────────
function Header({ user, onLogout, application, userMenuOpen, setUserMenuOpen, userMenuRef }) {
  const completed = application?.completedSections?.length || 0;
  const total = 6; // pii, driversLicense, income, incomeDocuments, rentalHistory, references
  return (
    <>
      <header className="bg-[#1a1a2e] px-6 py-4 flex items-center justify-between flex-shrink-0 z-50">
        <Link to="/" className="text-white font-medium text-lg tracking-wide">Nivasapro.ai</Link>
        <div className="flex items-center gap-5">
          {application?.property && (
            <div className="hidden sm:flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#1D9E75]" />
              <span className="text-xs text-gray-300">{application.property.name || application.property.address}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              {Array.from({ length: total }).map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full ${i < completed ? 'bg-[#1D9E75]' : 'bg-gray-600'}`} />
              ))}
            </div>
            <span className="text-xs text-gray-400">{completed}/{total}</span>
          </div>
          {user && (
            <div className="relative" ref={userMenuRef}>
              <button onClick={() => setUserMenuOpen(v => !v)} className="flex items-center gap-1.5 text-sm text-gray-300 hover:text-white py-1 px-2 rounded-lg hover:bg-white/10 transition-colors">
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">{user.email || user.username}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-xs text-gray-500 truncate">{user.email || user.username}</p>
                  </div>
                  <button onClick={onLogout} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors text-left">
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>
      <div className="h-1 bg-[#1D9E75] flex-shrink-0" />
    </>
  );
}

// ── step 0 — personal info ────────────────────────────────────────────────────
function StepPii({ pii, setPii, errors, saved }) {
  const set = field => e => setPii(p => ({ ...p, [field]: e.target.value }));
  const setSSN   = e => setPii(p => ({ ...p, ssn:   formatSSN(e.target.value) }));
  const setPhone = e => setPii(p => ({ ...p, phone: formatPhone(e.target.value) }));

  // Max date for DOB: today (must be born before today)
  const maxDob = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-5">

      {/* Privacy & purpose disclaimer */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-4 text-xs text-gray-600 leading-relaxed space-y-2">
        <p className="font-semibold text-gray-800 text-sm">Why we collect this information</p>
        <p>
          The information in this step is used to verify your identity. This ensures that your
          credit history and background report are accurate, secure, and protected. These reports
          are only shared with rental managers who have also verified their identities.
        </p>
        <p className="font-medium text-gray-700">You'll be asked to provide:</p>
        <ul className="list-disc list-inside space-y-1 pl-1">
          <li>Your full legal name, current address, and mobile phone number</li>
          <li>Your date of birth</li>
          <li>Your Social Security number or Individual Taxpayer Identification Number <span className="text-gray-400">(optional)</span></li>
          <li>Consent to terms from Experian and Contemporary Information Corp. (CIC) to perform credit and background checks</li>
        </ul>
        <p className="text-gray-500">
          Experian may also ask additional security questions to help confirm your identity.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="First Name" error={errors.firstName}>
          <Input value={pii.firstName} onChange={set('firstName')} placeholder="John" />
        </Field>
        <Field label="Last Name" error={errors.lastName}>
          <Input value={pii.lastName} onChange={set('lastName')} placeholder="Smith" />
        </Field>
      </div>
      <Field label="Date of Birth" error={errors.dob}>
        <Input type="date" value={pii.dob} onChange={set('dob')} max={maxDob} />
      </Field>
      <Field label={`Social Security Number${saved ? ' (leave blank to keep saved value)' : ''}`} error={errors.ssn}>
        <Input
          type="password"
          value={pii.ssn}
          onChange={setSSN}
          placeholder={saved ? '••••••••• (already saved)' : 'XXX-XX-XXXX'}
          autoComplete="off"
          inputMode="numeric"
          maxLength={11}
        />
        <p className="mt-1 text-[11px] text-gray-400">Encrypted at rest. Used for background & credit check only.</p>
      </Field>
      <Field label="Phone Number" error={errors.phone}>
        <Input
          type="tel"
          value={pii.phone}
          onChange={setPhone}
          placeholder="(555) 000-0000"
          inputMode="numeric"
          maxLength={14}
        />
      </Field>
      <Field label="Current Address" error={errors.currentAddress}>
        <Input value={pii.currentAddress} onChange={set('currentAddress')} placeholder="123 Main St, City, State, ZIP" />
      </Field>
    </div>
  );
}

// ── step 1 — driver's license ─────────────────────────────────────────────────
function StepDL({ dl, setDl, errors, saved, dlFile, dlUploading, onUpload, dlDoc }) {
  const set = field => e => setDl(d => ({ ...d, [field]: e.target.value }));
  const fileRef = useRef(null);
  const todayIso = new Date().toISOString().split('T')[0];
  return (
    <div className="space-y-5">
      <Field label={`License Number${saved ? ' (leave blank to keep saved value)' : ''}`} error={errors.licenseNumber}>
        <Input type="password" value={dl.licenseNumber} onChange={set('licenseNumber')} placeholder={saved ? '•••••••• (already saved)' : 'License number'} autoComplete="off" />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Issuing State" error={errors.issuingState}>
          <Select value={dl.issuingState} onChange={set('issuingState')}>
            <option value="">Select state…</option>
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="Expiry Date" error={errors.expiryDate}>
          <Input type="date" value={dl.expiryDate} onChange={set('expiryDate')} min={todayIso} />
        </Field>
      </div>

      <Field label="Driver's License Photo / Scan">
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-cyan-400 hover:bg-cyan-50 transition-colors"
        >
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={onUpload} />
          {dlUploading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
            </div>
          ) : dlDoc ? (
            <div className="flex items-center justify-center gap-2 text-sm text-[#1D9E75]">
              <CheckCircle2 className="w-4 h-4" /> Document uploaded
              <span className="text-gray-400 text-xs">(click to replace)</span>
            </div>
          ) : dlFile ? (
            <div className="flex items-center justify-center gap-2 text-sm text-[#1D9E75]">
              <CheckCircle2 className="w-4 h-4" /> {dlFile.name}
            </div>
          ) : (
            <>
              <Upload className="w-6 h-6 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Click to upload DL photo or scan</p>
              <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG accepted</p>
            </>
          )}
        </div>
      </Field>
    </div>
  );
}

// ── step 2 — employment & income ──────────────────────────────────────────────
function StepIncome({ income, setIncome, errors }) {
  const set = field => e => setIncome(i => ({ ...i, [field]: e.target.value }));
  const todayIso = new Date().toISOString().split('T')[0];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Employer" error={errors.employer}>
          <Input value={income.employer} onChange={set('employer')} placeholder="ACME Corp" />
        </Field>
        <Field label="Job Title" error={errors.jobTitle}>
          <Input value={income.jobTitle} onChange={set('jobTitle')} placeholder="Software Engineer" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Employment Type" error={errors.employmentType}>
          <Select value={income.employmentType} onChange={set('employmentType')}>
            <option value="">Select…</option>
            {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Field>
        <Field label="Employment Start Date" error={errors.startDate}>
          <Input type="date" value={income.startDate} onChange={set('startDate')} max={todayIso} />
        </Field>
      </div>
      <Field label="Annual Gross Income (USD)" error={errors.annualIncome}>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
          <Input type="number" value={income.annualIncome} onChange={set('annualIncome')} className="pl-7" placeholder="65000" min="0" />
        </div>
      </Field>

      {/* Employer contact — for verification purposes */}
      <div className="pt-2 border-t border-gray-100">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Employer Contact <span className="normal-case font-normal text-gray-400">(for verification)</span></p>
        <div className="space-y-4">
          <Field label="Contact Name">
            <Input value={income.contactName} onChange={set('contactName')} placeholder="HR manager or supervisor name" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Contact Phone (optional)">
              <Input type="tel" value={income.contactPhone} onChange={set('contactPhone')} placeholder="(555) 000-0000" />
            </Field>
            <Field label="Contact Email (optional)">
              <Input type="email" value={income.contactEmail} onChange={set('contactEmail')} placeholder="hr@company.com" />
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── step 3 — income documents ─────────────────────────────────────────────────
function StepDocs({ incDocs, incUpload, incUploading, onUpload, errors }) {
  const fileRef = useRef(null);
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500">Upload at least one recent pay stub, tax return, or bank statement as proof of income.</p>

      {/* existing docs */}
      {incDocs.length > 0 && (
        <div className="space-y-2">
          {incDocs.map((d, i) => (
            <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3 text-sm">
              <Paperclip className="w-4 h-4 text-[#1D9E75] flex-shrink-0" />
              <span className="text-gray-700 truncate">{d.fileName}</span>
              <span className="ml-auto text-xs text-[#1D9E75] font-medium">Uploaded</span>
            </div>
          ))}
        </div>
      )}

      {/* upload area */}
      <div
        onClick={() => fileRef.current?.click()}
        className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-cyan-400 hover:bg-cyan-50 transition-colors"
      >
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={onUpload} />
        {incUploading ? (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Uploading {incUpload?.name}…
          </div>
        ) : (
          <>
            <Upload className="w-6 h-6 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">{incDocs.length > 0 ? 'Upload another document' : 'Click to upload income document'}</p>
            <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG: pay stub, W-2, tax return, or bank statement</p>
          </>
        )}
      </div>
      {errors.incDocs && <p className="text-xs text-red-500">{errors.incDocs}</p>}
    </div>
  );
}

// ── step 4 — rental history ───────────────────────────────────────────────────
function StepRentalHistory({ rentals, setRentals, firstTime, setFirstTime }) {
  function update(idx, field, val) {
    setRentals(rs => rs.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  }
  function addEntry() { setRentals(rs => [...rs, { ...BLANK_RENTAL }]); }
  function removeEntry(idx) { setRentals(rs => rs.filter((_, i) => i !== idx)); }

  return (
    <div className="space-y-5">
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={firstTime}
          onChange={e => { setFirstTime(e.target.checked); if (e.target.checked) setRentals([]); else setRentals([{ ...BLANK_RENTAL }]); }}
          className="w-4 h-4 rounded border-gray-300 text-[#1D9E75] focus:ring-cyan-500"
        />
        <span className="text-sm text-gray-700">I am a first-time renter (no prior rental history)</span>
      </label>

      {!firstTime && rentals.map((r, idx) => (
        <div key={idx} className="border border-gray-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-700">Rental #{idx + 1}</h4>
            {rentals.length > 1 && (
              <button onClick={() => removeEntry(idx)} className="text-red-400 hover:text-red-600 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          <Field label="Address"><Input value={r.address} onChange={e => update(idx, 'address', e.target.value)} placeholder="123 Oak St, City, State" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Landlord Name"><Input value={r.landlordName} onChange={e => update(idx, 'landlordName', e.target.value)} /></Field>
            <Field label="Landlord Phone"><Input type="tel" value={r.landlordPhone} onChange={e => update(idx, 'landlordPhone', e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Monthly Rent">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <Input type="number" value={r.monthlyRent} onChange={e => update(idx, 'monthlyRent', e.target.value)} className="pl-7" placeholder="1200" />
              </div>
            </Field>
            <Field label="Move-in Date"><Input type="date" value={r.moveInDate} onChange={e => update(idx, 'moveInDate', e.target.value)} /></Field>
            <Field label="Move-out Date"><Input type="date" value={r.moveOutDate} onChange={e => update(idx, 'moveOutDate', e.target.value)} /></Field>
          </div>
          <Field label="Reason for Leaving"><Input value={r.reasonForLeaving} onChange={e => update(idx, 'reasonForLeaving', e.target.value)} /></Field>
        </div>
      ))}

      {!firstTime && (
        <button onClick={addEntry} className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700 transition-colors">
          <PlusCircle className="w-4 h-4" /> Add another rental
        </button>
      )}
    </div>
  );
}

// ── step 5 — references ───────────────────────────────────────────────────────
function StepReferences({ refs, setRefs }) {
  function update(idx, field, val) {
    setRefs(rs => rs.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  }
  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">Provide two personal or professional references (not family members).</p>
      {refs.map((r, idx) => (
        <div key={idx} className="border border-gray-200 rounded-xl p-5 space-y-4">
          <h4 className="text-sm font-medium text-gray-700">Reference {idx + 1}</h4>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Full Name"><Input value={r.name} onChange={e => update(idx, 'name', e.target.value)} /></Field>
            <Field label="Relationship"><Input value={r.relationship} onChange={e => update(idx, 'relationship', e.target.value)} placeholder="Colleague, Manager…" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Phone"><Input type="tel" value={r.phone} onChange={e => update(idx, 'phone', e.target.value)} /></Field>
            <Field label="Email"><Input type="email" value={r.email} onChange={e => update(idx, 'email', e.target.value)} /></Field>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── step 6 — review ───────────────────────────────────────────────────────────
function StepReview({ application, pii, income, incDocs, rentals, firstTime, refs, onEdit }) {
  const completed = application?.completedSections || [];
  function Row({ label, value }) {
    return value ? <div className="flex gap-2 text-sm"><span className="text-gray-500 w-36 flex-shrink-0">{label}</span><span className="text-gray-800">{value}</span></div> : null;
  }
  function Section({ title, sectionKey, step, children }) {
    const done = completed.includes(sectionKey);
    return (
      <div className="border border-gray-200 rounded-xl p-4 space-y-2">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
            {done && <span className="text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5">Saved</span>}
          </div>
          <button onClick={() => onEdit(step)} className="text-xs text-cyan-600 hover:underline">Edit</button>
        </div>
        {children}
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">Review your information before submitting.</p>

      <Section title="Personal Information" sectionKey="pii" step={0}>
        <Row label="Name" value={`${pii.firstName} ${pii.lastName}`.trim()} />
        <Row label="Date of Birth" value={pii.dob} />
        <Row label="Phone" value={pii.phone} />
        <Row label="Address" value={pii.currentAddress} />
        <Row label="SSN" value={completed.includes('pii') ? '•••••••••' : 'Not saved'} />
      </Section>

      <Section title="Driver's License" sectionKey="driversLicense" step={1}>
        <Row label="License #" value={completed.includes('driversLicense') ? '••••••••' : 'Not saved'} />
        <Row label="State / Expiry" value={application?.driversLicense?.issuingState && `${application.driversLicense.issuingState}, expires ${application.driversLicense.expiryDate || ''}`} />
        <Row label="Document" value={application?.driversLicense?.documentUrl ? 'Uploaded ✓' : 'Not uploaded'} />
      </Section>

      <Section title="Employment & Income" sectionKey="income" step={2}>
        <Row label="Employer" value={income.employer} />
        <Row label="Title" value={income.jobTitle} />
        <Row label="Type" value={income.employmentType} />
        <Row label="Annual Income" value={income.annualIncome ? `$${Number(income.annualIncome).toLocaleString()}` : ''} />
      </Section>

      <Section title="Income Documents" sectionKey="incomeDocuments" step={3}>
        {incDocs.length > 0
          ? incDocs.map((d, i) => <Row key={i} label={`Document ${i + 1}`} value={d.fileName} />)
          : <p className="text-xs text-red-500">No documents uploaded. Required before submitting</p>}
      </Section>

      <Section title="Rental History" sectionKey="rentalHistory" step={4}>
        {firstTime
          ? <p className="text-sm text-gray-600">First-time renter</p>
          : rentals.map((r, i) => <Row key={i} label={`Rental ${i + 1}`} value={r.address} />)}
      </Section>

      <Section title="References" sectionKey="references" step={5}>
        {refs.map((r, i) => <Row key={i} label={`Reference ${i + 1}`} value={r.name ? `${r.name} (${r.relationship})` : ''} />)}
      </Section>
    </div>
  );
}

// ── Application Status Page (post-submission) ─────────────────────────────────
function ApplicationStatusPage({ user, application, pii, onLogout, onLogin, userMenuOpen, setUserMenuOpen, userMenuRef }) {
  const propertyLabel = application?.property?.name || application?.property?.address || 'your property';
  const propertyAddress = application?.property?.name ? application?.property?.address : null;
  const orgName = application?.organization?.companyName || application?.organization?.name || null;
  const firstName = pii?.firstName || user?.name?.split(' ')[0] || 'there';
  const status = application?.status || 'submitted';

  const [messages, setMessages]     = useState([]);
  const [msgsLoading, setMsgsLoading] = useState(true);
  const [input, setInput]           = useState('');
  const [sending, setSending]       = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [activating, setActivating] = useState(false);
  const [error, setError]           = useState('');
  const messagesEndRef              = useRef(null);
  const pollRef                     = useRef(null);
  const fileInputRef                = useRef(null);

  const isApproved = status === 'approved';

  async function handleGoToPortal() {
    setActivating(true);
    try {
      const freshUser = await apiClient.get('/api/auth/me');
      // Replace the stale Applicant JWT with the fresh Tenant JWT before updating React state
      if (freshUser.data.token) localStorage.setItem('token', freshUser.data.token);
      if (onLogin) onLogin(freshUser.data.user);
    } catch {
      // session may have changed — force a full reload as fallback
      window.location.href = '/niva';
    }
  }

  const statusSteps = [
    { key: 'submitted',    label: 'Submitted',     done: true },
    { key: 'under_review', label: 'Under Review',  done: ['under_review','approved','rejected'].includes(status) },
    { key: 'approved',     label: 'Decision',      done: ['approved','rejected'].includes(status) },
  ];

  useEffect(() => {
    if (!application?._id) { setMsgsLoading(false); return; }
    loadMessages();
    // Poll every 12s for new messages from Niva
    pollRef.current = setInterval(loadMessages, 12000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application?._id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function loadMessages() {
    if (!application?._id) return;
    try {
      const res = await apiClient.get(`/api/application-messages/${application._id}`);
      setMessages(res.data);
    } catch {
      // Silently fail — messages may not exist yet
    } finally {
      setMsgsLoading(false);
    }
  }

  async function handleSend() {
    const text = input.trim();
    if (!text || sending || !application?._id) return;
    setSending(true);
    setError('');
    // Optimistic: add applicant message immediately
    const optimistic = { _id: Date.now(), senderType: 'Applicant', content: text, createdAt: new Date().toISOString() };
    setMessages(m => [...m, optimistic]);
    setInput('');
    try {
      const res = await apiClient.post(`/api/application-messages/${application._id}`, { content: text });
      // Replace optimistic + add Niva response
      setMessages(m => {
        const without = m.filter(x => x._id !== optimistic._id);
        const toAdd = [res.data.applicantMessage, res.data.nivaMessage].filter(Boolean);
        return [...without, ...toAdd];
      });
    } catch (err) {
      setMessages(m => m.filter(x => x._id !== optimistic._id));
      setError('Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  }

  async function handleFileUpload(e) {
    const file = e.target.files?.[0];
    if (!file || !application?._id) return;
    e.target.value = '';
    setUploading(true);
    setError('');
    try {
      // 1. Get signed upload URL
      const { data: urlData } = await apiClient.post('/api/applications/me/upload-url', {
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        applicationId: application._id,
        docType: 'income',
      });
      // 2. Upload directly to GCS
      await fetch(urlData.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      // 3. Register document
      await apiClient.post('/api/applications/me/add-document', {
        docType: 'income',
        fileUrl: urlData.blobPath,
        fileName: file.name,
        fileType: file.type || 'application/octet-stream',
      });
      // 4. Trigger rescore (Niva follow-up message generated on backend)
      await apiClient.post('/api/applications/me/rescore');
      // 5. Reload messages — Niva will have responded
      await loadMessages();
    } catch (err) {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  function formatTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div className="min-h-screen bg-[#f4f4f0] flex flex-col">
      <Header user={user} onLogout={onLogout} application={application}
        userMenuOpen={userMenuOpen} setUserMenuOpen={setUserMenuOpen} userMenuRef={userMenuRef} />

      <div className="flex-1 flex flex-col max-w-2xl mx-auto w-full px-4 py-6 gap-4 overflow-hidden" style={{ height: 'calc(100vh - 61px)' }}>

        {/* ── Property context banner ── */}
        {application?.property && (
          <div className="bg-white rounded-2xl border border-gray-200 px-5 py-4 flex items-center gap-4 flex-shrink-0">
            <div className="w-9 h-9 rounded-xl bg-[#1a1a2e] flex items-center justify-center flex-shrink-0">
              <Home className="w-4 h-4 text-[#1D9E75]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Property</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{propertyLabel}</p>
              {propertyAddress && (
                <p className="text-xs text-gray-500 flex items-center gap-1 truncate">
                  <MapPin className="w-3 h-3 flex-shrink-0" />{propertyAddress}
                </p>
              )}
            </div>
            {orgName && (
              <div className="hidden sm:flex flex-col items-end gap-0.5 flex-shrink-0">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Managed by</p>
                <div className="flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-xs font-medium text-gray-700">{orgName}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Status bar ── */}
        <div className="bg-white rounded-2xl border border-gray-200 px-5 py-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Application status</p>
              <p className="text-sm font-semibold text-gray-900">{propertyLabel}</p>
            </div>
            <StatusDot status={status} />
          </div>
          {/* Progress steps */}
          <div className="flex items-center gap-0">
            {statusSteps.map((s, i) => (
              <React.Fragment key={s.key}>
                <div className="flex flex-col items-center gap-1">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${s.done ? 'bg-[#1D9E75]' : 'bg-gray-200'}`}>
                    {s.done ? <Check className="w-3 h-3 text-white" /> : <div className="w-2 h-2 rounded-full bg-gray-400" />}
                  </div>
                  <span className={`text-[10px] whitespace-nowrap ${s.done ? 'text-[#1D9E75] font-medium' : 'text-gray-400'}`}>{s.label}</span>
                </div>
                {i < statusSteps.length - 1 && (
                  <div className={`flex-1 h-0.5 mb-3 mx-1 ${statusSteps[i+1]?.done ? 'bg-[#1D9E75]' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── Approved banner ── */}
        {isApproved && (
          <div className="bg-white rounded-2xl border border-[#1D9E75] px-5 py-5 flex-shrink-0">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#f0fdf4] flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-[#1D9E75]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">Welcome home, {firstName}!</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Your application has been approved. Your tenant account is now active. Head to your portal to access maintenance, lease, and more.
                </p>
                <button
                  onClick={handleGoToPortal}
                  disabled={activating}
                  className="mt-3 flex items-center gap-2 bg-[#1D9E75] hover:bg-[#178a65] text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  {activating
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Opening portal…</>
                    : <><Sparkles className="w-3.5 h-3.5" /> Go to Tenant Portal</>
                  }
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Niva conversation ── */}
        <div className="flex-1 bg-white rounded-2xl border border-gray-200 flex flex-col overflow-hidden min-h-0">
          {/* Chat header */}
          <div className="bg-[#1a1a2e] px-5 py-3 flex items-center gap-3 flex-shrink-0 rounded-t-2xl">
            <div className="w-8 h-8 rounded-full bg-[#1D9E75]/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#1D9E75]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Niva</p>
              <p className="text-[10px] text-gray-400">Your application coordinator</p>
            </div>
            <button
              onClick={loadMessages}
              className="ml-auto text-gray-500 hover:text-gray-300 transition-colors"
              title="Refresh messages"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {msgsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 text-gray-300 animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-3">
                  <Clock className="w-5 h-5 text-gray-300" />
                </div>
                <p className="text-sm text-gray-500 font-medium">Niva is reviewing your application</p>
                <p className="text-xs text-gray-400 mt-1">A message will arrive here shortly</p>
              </div>
            ) : (
              messages.map((msg, i) => {
                const isNiva = msg.senderType === 'Niva';
                return (
                  <div key={msg._id || i} className={`flex gap-2.5 ${isNiva ? '' : 'flex-row-reverse'}`}>
                    {/* Avatar */}
                    <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5 ${isNiva ? 'bg-[#1a1a2e]' : 'bg-gray-200'}`}>
                      {isNiva
                        ? <Sparkles className="w-3.5 h-3.5 text-[#1D9E75]" />
                        : <User className="w-3.5 h-3.5 text-gray-500" />}
                    </div>
                    {/* Bubble */}
                    <div className={`max-w-[80%] ${isNiva ? '' : ''}`}>
                      <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                        isNiva
                          ? 'bg-[#f0fdf4] text-gray-800 rounded-tl-sm border border-green-100'
                          : 'bg-[#1a1a2e] text-white rounded-tr-sm'
                      }`}>
                        {msg.content}
                      </div>
                      <p className={`text-[10px] text-gray-400 mt-0.5 ${isNiva ? 'ml-1' : 'text-right mr-1'}`}>
                        {formatTime(msg.createdAt)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            {sending && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 rounded-full bg-[#1a1a2e] flex items-center justify-center">
                  <Sparkles className="w-3.5 h-3.5 text-[#1D9E75]" />
                </div>
                <div className="bg-[#f0fdf4] border border-green-100 rounded-2xl rounded-tl-sm px-4 py-2.5 flex gap-1 items-center">
                  {[0,120,240].map(d => (
                    <div key={d} className="w-1.5 h-1.5 rounded-full bg-[#1D9E75] animate-bounce" style={{ animationDelay: `${d}ms` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Error */}
          {error && (
            <div className="px-4 pb-2">
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />{error}
              </p>
            </div>
          )}

          {/* Input */}
          <div className="border-t border-gray-100 px-4 py-3 flex-shrink-0">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              className="hidden"
              onChange={handleFileUpload}
            />
            <div className="flex gap-2 items-end">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || sending}
                className="p-2.5 text-gray-400 hover:text-[#1D9E75] hover:bg-gray-50 rounded-xl transition-colors disabled:opacity-40 flex-shrink-0"
                title="Upload a document"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin text-[#1D9E75]" /> : <Paperclip className="w-4 h-4" />}
              </button>
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Ask Niva a question or provide more information…"
                rows={2}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white resize-none"
                disabled={sending || uploading}
              />
              <button
                onClick={handleSend}
                disabled={sending || uploading || !input.trim()}
                className="px-4 py-2.5 bg-[#1D9E75] hover:bg-[#178a65] text-white rounded-xl transition-colors disabled:opacity-40 flex items-center gap-1.5 flex-shrink-0"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5 text-center">
              Niva handles all application queries on behalf of the landlord · <span className="text-gray-400">PDF, PNG, JPG accepted</span>
            </p>
          </div>
        </div>

        {/* ── What's ahead (compact) ── */}
        <div className="bg-white rounded-2xl border border-gray-200 px-5 py-4 flex-shrink-0">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-[#1D9E75]" />
            When you move in, you'll have
          </p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { e: '🛠️', t: 'Online maintenance' },
              { e: '📄', t: 'Lease on demand' },
              { e: '💬', t: 'Direct messaging' },
              { e: '✨', t: 'AI assistant 24/7' },
            ].map(f => (
              <div key={f.t} className="flex items-center gap-2 text-xs text-gray-600">
                <span>{f.e}</span>{f.t}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

function StatusDot({ status }) {
  const map = {
    submitted:    { label: 'Under Review', color: 'bg-amber-400' },
    under_review: { label: 'Under Review', color: 'bg-blue-400' },
    approved:     { label: 'Approved',     color: 'bg-green-500' },
    rejected:     { label: 'Not Approved', color: 'bg-red-400' },
    in_progress:  { label: 'In Progress',  color: 'bg-gray-400' },
  };
  const cfg = map[status] || map.submitted;
  return (
    <span className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
      <span className={`w-2 h-2 rounded-full ${cfg.color} animate-pulse`} />
      {cfg.label}
    </span>
  );
}
